<template>
    <ProductModalTitle>신규 품목 등록</ProductModalTitle>

    <FormContainer>
        <!-- ! 왼쪽 섹션 -->
        <FormSection>
            <Card>
                <CardHeader>
                    <h3>품목 이미지 등록</h3>
                    <div style="display: flex; gap: 12px;">
                        <CustomFileUpload for="product-image">파일 선택
                            <Plus :size="20" style="padding-top: 5px" />
                        </CustomFileUpload>
                        <CustomFileUpload @click="openImageSearchModal"
                            style="background: #28a745; border-color: #28a745;">이미지 검색
                            <Magnify :size="20" style="padding-top: 5px" />
                        </CustomFileUpload>
                    </div>
                    <FileInput type="file" id="product-image" @change="handleImageChange" accept="image/*" />
                </CardHeader>
                <CardBody>
                    <CardSection>
                        <FormGroup>
                            <Label>이미지</Label>
                            <FileInputWrapper>
                                <FileName>{{ fileName || (selectedImageUrl ? '검색 이미지 선택됨' : '선택된 파일 없음') }}</FileName>
                            </FileInputWrapper>
                        </FormGroup>
                        <ImagePreview v-if="imagePreview">
                            <img :src="imagePreview" alt="선택한 이미지 미리보기" />
                        </ImagePreview>
                    </CardSection>

                    <CardSection>
                        <FormGroup>
                            <Label>해시태그</Label>
                            <Input v-model="hashtagInput" placeholder="입력 후 엔터를 눌러주세요." @keyup.enter="addHashtag" />
                        </FormGroup>
                        <HashtagContainer v-if="hashtags.length > 0">
                            <HashtagItem v-for="(tag, index) in hashtags" :key="index">
                                #{{ tag }}
                                <DeleteButton @click="removeHashtag(index)">&times;</DeleteButton>
                            </HashtagItem>
                        </HashtagContainer>
                    </CardSection>
                </CardBody>
            </Card>

            <CategoryCard>
                <CardHeader>
                    <h3>카테고리 설정</h3>
                    <CustomFileUpload @click="openCategoryModal">카테고리 추가
                        <Plus :size="20" style="padding-top: 5px" />
                    </CustomFileUpload>
                </CardHeader>
                <SetCardBody>
                    <CardSection>
                        <FormGroup>
                            <Label>검색</Label>
                            <SearchWrapper class="search-wrapper">
                                <SearchInputWrapper>
                                    <Input v-model="searchQuery" @input="handleSearch" @focus="showSearchResults = true"
                                        placeholder="카테고리명을 입력하세요" />
                                    <SearchButton @click="handleSearch">
                                        <span class="material-icons">
                                            <Magnify />
                                        </span>
                                    </SearchButton>
                                </SearchInputWrapper>
                                <SearchResults v-if="showSearchResults && filteredItems.length > 0">
                                    <SearchResultItem v-for="(item, index) in filteredItems" :key="index"
                                        @click="selectItem(item)">
                                        <CategoryPath v-if="item.type === 'category2'">
                                            {{ item.name1 }} > {{ item.name2 }}
                                        </CategoryPath>
                                        <CategoryPath v-else>
                                            {{ item.name1 }}
                                        </CategoryPath>
                                    </SearchResultItem>
                                </SearchResults>
                            </SearchWrapper>
                        </FormGroup>

                        <FormGroup>
                            <Label required>카테고리1</Label>
                            <Select v-model="selectedCategory1" @change="onCategory1Change">
                                <option value="">선택하세요</option>
                                <option v-for="category in category1List" :key="category.num" :value="category.num">
                                    {{ category.name }}
                                </option>
                            </Select>
                        </FormGroup>

                        <FormGroup>
                            <Label required>카테고리2</Label>
                            <Select v-model="selectedCategory2" :disabled="!selectedCategory1">
                                <option value="">선택하세요</option>
                                <option v-for="category in category2List" :key="category.num" :value="category.num">
                                    {{ category.name }}
                                </option>
                            </Select>
                        </FormGroup>
                    </CardSection>
                </SetCardBody>
            </CategoryCard>
        </FormSection>

        <!-- ! 오른쪽 섹션 -->
        <FormSection>
            <SetCard>
                <CardHeader style="height: 30px;">
                    <h3>품목 정보 입력</h3>
                </CardHeader>
                <CardBody>
                    <StepContentWrapper>
                        <!-- * 기본 정보 -->
                        <StepColumn :isBasicInfo="true">
                            <StepColumnTitle>기본 정보</StepColumnTitle>
                            <FormGroup>
                                <Label required>품목명</Label>
                                <Input v-model="newProduct.name3"
                                    @input="e => newProduct.name3 = e.target.value.toUpperCase()" />
                            </FormGroup>
                            <FormGroup>
                                <Label>모델명</Label>
                                <Input v-model="newProduct.name4"
                                    @input="e => newProduct.name4 = e.target.value.toUpperCase()" />
                            </FormGroup>
                            <FormGroup>
                                <Label>품목코드</Label>
                                <Input v-model="newProduct.code"
                                    @input="e => newProduct.code = e.target.value.toUpperCase()" />
                            </FormGroup>
                            <FormGroup>
                                <Label>색상</Label>
                                <Select v-model="newProduct.color">
                                    <option value="">선택하세요</option>
                                    <optgroup v-for="(colorList, groupName) in colors" :key="groupName"
                                        :label="groupName">
                                        <option v-for="color in colorList" :key="color" :value="color">{{ color }}
                                        </option>
                                    </optgroup>
                                </Select>
                            </FormGroup>
                            <FormGroup>
                                <Label>사이즈</Label>
                                <Input v-model="newProduct.size"
                                    @input="e => newProduct.size = e.target.value.toUpperCase()" />
                            </FormGroup>
                            <RadioFormGroup>
                                <Label required>대상</Label>
                                <RadioGroup>
                                    <RadioLabel>
                                        <input type="radio" v-model="newProduct.target" value="구입"> 구입
                                    </RadioLabel>
                                    <RadioLabel>
                                        <input type="radio" v-model="newProduct.target" value="대여"> 대여
                                    </RadioLabel>
                                </RadioGroup>
                            </RadioFormGroup>
                            <RadioFormGroup>
                                <Label required>과세구분</Label>
                                <RadioGroup>
                                    <RadioLabel>
                                        <input type="radio" v-model="newProduct.tax" value="과세"> 과세
                                    </RadioLabel>
                                    <RadioLabel>
                                        <input type="radio" v-model="newProduct.tax" value="영세"> 영세
                                    </RadioLabel>
                                </RadioGroup>
                            </RadioFormGroup>
                        </StepColumn>

                        <!-- * 가격 정보 -->
                        <StepColumn>
                            <StepColumnTitle>수량 및 가격 정보</StepColumnTitle>
                            <RadioFormGroup>
                                <Label required>단위 유형</Label>
                                <RadioGroup>
                                    <RadioLabel>
                                        <input type="radio" v-model="newProduct.type" value="낱개"> 낱개
                                    </RadioLabel>
                                    <RadioLabel>
                                        <input type="radio" v-model="newProduct.type" value="박스"> 박스
                                    </RadioLabel>
                                </RadioGroup>
                            </RadioFormGroup>
                            <FormGroup>
                                <Label required>{{ newProduct.type === '박스' ? '박스 수량' : '품목 수량' }}</Label>
                                <QuantityInput v-model.number="newProduct.quantity" type="number" required />개
                            </FormGroup>
                            <FormGroup v-if="newProduct.type === '박스'">
                                <Label required>박스당 개수</Label>
                                <QuantityInput v-model.number="newProduct.pieces_per_box" type="number" required />개
                            </FormGroup>
                            <FormGroup v-if="newProduct.type === '박스'">
                                <Label>낱개 수량</Label>
                                <QuantityInput v-model.number="newProduct.piece_quantity" type="number" />개
                            </FormGroup>
                            <FormGroup>
                                <Label>합계 수량</Label>
                                <QuantityInput :value="computedAllQuantity" type="number" disabled
                                    style="background-color: #f5f5f5;" />개
                            </FormGroup>
                            <FormGroup>
                                <Label>구입가격</Label>
                                <PriceInput v-model.number="newProduct.price" type="number" />원
                            </FormGroup>
                            <FormGroup>
                                <Label>대여가격</Label>
                                <PriceInput v-model.number="newProduct.price2" type="number" />원
                            </FormGroup>
                            <FormGroup>
                                <Label>공급가격</Label>
                                <PriceInput v-model.number="newProduct.price3" type="number" />원
                            </FormGroup>
                        </StepColumn>

                        <!-- * 상세 정보 -->
                        <StepColumn>
                            <StepColumnTitle>상세 정보</StepColumnTitle>
                            <FormGroup>
                                <Label>바코드</Label>
                                <Input v-model="newProduct.bcode" />
                            </FormGroup>
                            <FormGroup>
                                <Label>보험코드</Label>
                                <Input v-model="newProduct.icode" placeholder="당뇨용품일 경우 입력" />
                            </FormGroup>
                            <FormGroup>
                                <Label>제조사명</Label>
                                <Input v-model="newProduct.company" />
                            </FormGroup>
                            <FormGroup>
                                <Label>재고위치</Label>
                                <Input v-model="newProduct.location1" />-<Input v-model="newProduct.location2" />
                            </FormGroup>
                            <FormGroup>
                                <Label>비고</Label>
                                <Textarea v-model="newProduct.note" />
                            </FormGroup>
                        </StepColumn>
                    </StepContentWrapper>
                </CardBody>
            </SetCard>

            <ButtonArea>
                <SubmitButton @click="openSetModal">
                    <Apps :size="20" style="padding-top: 3px" />세트 만들기
                </SubmitButton>
                <SubmitButton @click="addNewProduct">
                    <CheckBold :size="20" style="padding-top: 3px" />입력 완료
                </SubmitButton>
            </ButtonArea>
        </FormSection>
    </FormContainer>

    <!-- ! 카테고리 모달 -->
    <ModalPage v-if="showCategoryModal" v-model:show="showCategoryModal" :width="modal_width.category"
        :height="modal_height.category">
        <CategoryModal @close="closeCategoryModal" @category-added="handleCategoryAdded"
            @category-updated="handleCategoryUpdated" />
    </ModalPage>

    <!-- ! 세트 모달 -->
    <ModalPage v-if="showSetModal" v-model:show="showSetModal" :width="modal_width.set" :height="modal_height.set">
        <SetModal @close="closeSetModal" @category-added="handleCategoryAdded"
            @category-updated="handleCategoryUpdated" />
    </ModalPage>

    <!-- ! 이미지 검색 모달 -->
    <ImageSearchModal v-if="showImageSearchModal" :initialSearchQuery="newProduct.name3" @close="closeImageSearchModal"
        @imageSelected="onImageSelected" />
</template>

<script>
import * as ProductAddNewCss from '@/assets/styles/product/modal/ProductAddModalCss.js';
import ModalPage from '@/components/layout/ModalPage.vue';
import CategoryModal from '@/components/product/modal/CategoryModal.vue';
import SetModal from '@/components/product/modal/SetModal.vue';
import ImageSearchModal from '@/components/product/modal/ImageSearchModal.vue';

import Magnify from 'vue-material-design-icons/Magnify.vue';
import Plus from 'vue-material-design-icons/Plus.vue';
import Apps from 'vue-material-design-icons/Apps.vue';
import CheckBold from 'vue-material-design-icons/CheckBold.vue';

export default {

    name: 'ProductAddModal',

    emits: ['productAdded', 'closeModal', 'close'],

    components: {
        ...ProductAddNewCss,
        ModalPage,
        CategoryModal,
        SetModal,
        ImageSearchModal,
        Magnify, Plus, Apps, CheckBold
    },

    data() {
        return {
            newProduct: {
                name0: '',
                name1: '',
                name2: '',
                name3: '',
                name4: '',
                target: '구입',
                bcode: '',
                code: '',
                price: '',
                price2: '',
                price3: '',
                color: '',
                size: '',
                type: '낱개', // 품목 유형
                quantity: '',
                pieces_per_box: '',
                piece_quantity: '',
                all_quantity: '',
                note: '',
                company: '',
                icode: '',
                companykey: this.$globalState.company_key,
                tax: '과세',
                hashtags: [],
                location1: '',
                location2: ''
            },
            fileName: '',
            imagePreview: null,
            imageFile: null,
            selectedImageUrl: null, // 검색된 이미지 URL
            selectedImageTitle: null, // 검색된 이미지 제목
            hashtags: [],
            hashtagInput: '',
            colors: {
                기본색상: ["레드", "주황", "옐로우", "그린", "블루", "블랙", "화이트"],
                파스텔: ["연핑크", "연보라", "민트", "연베이지", "연브라운"],
                무늬: ["체크", "꽃무늬", "줄무늬", "블랙꽃무늬", "핑크꽃무늬", "별무늬"],
                어두운색상: ["남색", "버건디", "진회색", "와인", "진베이지", "군청"],
                밝은색상: ["아이보리", "베이지", "실버", "골드"]
            },
            searchQuery: '',
            showSearchResults: false,
            filteredItems: [],
            isAutoFilling: false,
            currentStep: 1,
            steps: [
                { title: '기본 정보' },
                { title: '가격 정보' },
                { title: '상세 정보' },
            ],
            category1List: [],    // 카테고리1 목록
            category2List: [],    // 카테고리2 목록
            allCategories: [],    // 카테고리 전체 목록
            selectedCategory1: '', // 선택된 카테고리1
            selectedCategory2: '', // 선택된 카테고리2
            showCategoryModal: false,
            showSetModal: false,
            showImageSearchModal: false,
            modal_width: {
                category: '800px',  // 카테고리 모달의 너비
                set: '70%'  // 세트 모달의 너비
            },
            modal_height: {
                category: '500px',  // 카테고리 모달의 높이
                set: '80%'  // 세트 모달의 높이
            },
        };
    },

    async created() {
        // ESC 키 이벤트 리스너 추가
        document.addEventListener('keydown', this.handleEscKey);
        await this.fetchAllCategories();
    },

    mounted() {
        document.addEventListener('click', this.handleClickOutside);
    },

    beforeUnmount() {
        // 컴포넌트가 제거될 때 이벤트 리스너 제거
        document.removeEventListener('keydown', this.handleEscKey);
        document.removeEventListener('click', this.handleClickOutside);
    },

    computed: {
        computedAllQuantity() {
            if (this.newProduct.type === '낱개') {
                // 낱개일 경우 품목 수량이 곧 합계 수량
                return this.newProduct.quantity || 0;
            } else {
                // 박스일 경우 (박스수량 * 박스당 개수) + 낱개 수량
                const boxQuantity = this.newProduct.quantity || 0;
                const piecesPerBox = this.newProduct.pieces_per_box || 0;
                const pieceQuantity = this.newProduct.piece_quantity || 0;

                return (boxQuantity * piecesPerBox) + pieceQuantity;
            }
        }
    },

    methods: {
        // 이미지 검색 모달 열기
        openImageSearchModal() {
            this.showImageSearchModal = true;
        },

        // 이미지 검색 모달 닫기
        closeImageSearchModal() {
            this.showImageSearchModal = false;
        },

        openCategoryModal() {
            this.showCategoryModal = true;
        },

        closeCategoryModal() {
            this.showCategoryModal = false;
        },

        openSetModal() {
            this.showSetModal = true;
        },

        closeSetModal() {
            this.showSetModal = false;
        },

        async handleCategoryAdded(categoryData) {
            // 전체 카테고리 목록 새로고침
            await this.fetchAllCategories();

            if (categoryData.category1Id) {
                this.selectedCategory1 = categoryData.category1Id;
                // 카테고리2 목록 업데이트
                await this.onCategory1Change();

                if (categoryData.category2Id) {
                    this.selectedCategory2 = categoryData.category2Id;
                }
            }

            // 모달 닫기
            this.closeCategoryModal();
        },

        async handleCategoryUpdated() {
            await this.fetchAllCategories();
        },

        async fetchCategory1List() {
            try {
                const response = await this.$axios.get(
                    `${this.$apiUrls.hanqmedi}/category/level1`,
                    {
                        params: {
                            companykey: this.$globalState.company_key
                        }
                    }
                );
                if (response.data.success) {
                    this.category1List = response.data.data;
                } else {
                    throw new Error(response.data.message || '카테고리1 조회 실패');
                }
            } catch (error) {
                console.error('카테고리1 데이터 조회 실패:', error);
                this.$swal.fire({
                    title: '오류',
                    text: '카테고리1 데이터를 불러오는데 실패했습니다.',
                    icon: 'error',
                    confirmButtonColor: '#fb8b24'
                });
            }
        },

        async fetchAllCategories() {
            try {
                const response = await this.$axios.get(
                    `${this.$apiUrls.hanqmedi}/getAllCategories`,
                    {
                        params: {
                            companykey: this.$globalState.company_key
                        }
                    }
                );

                console.log('전체 카테고리 데이터 조회 성공:', response.data);

                if (response.data.success) {
                    this.allCategories = response.data.data;
                    this.category1List = this.allCategories; // 카테고리1 목록 설정
                }
            } catch (error) {
                console.error('전체 카테고리 데이터 조회 실패:', error);
                this.$swal.fire({
                    title: '오류',
                    text: '카테고리 데이터를 불러오는데 실패했습니다.',
                    icon: 'error',
                    confirmButtonColor: '#fb8b24'
                });
            }
        },

        // 카테고리1 선택 시 해당하는 카테고리2 목록 조회
        async onCategory1Change() {
            try {
                this.selectedCategory2 = '';
                this.category2List = [];

                if (!this.selectedCategory1) return;

                // 선택된 카테고리1의 하위 카테고리 찾기
                const selectedCategory = this.category1List.find(
                    cat => cat.num === parseInt(this.selectedCategory1)
                );

                if (selectedCategory && selectedCategory.subcategories) {
                    this.category2List = selectedCategory.subcategories;
                }
            } catch (error) {
                console.error('카테고리2 목록 업데이트 중 오류 발생:', error);
                this.$swal.fire({
                    title: '오류',
                    text: '카테고리2 목록을 불러오는데 실패했습니다.',
                    icon: 'error',
                    confirmButtonColor: '#fb8b24'
                });
            }
        },
        // -----------------------------------------------
        // 프로세스 메서드
        // -----------------------------------------------
        isStepAccessible(step) {
            if (step === 1) return true;

            const isStep1Complete = !!(this.newProduct.name3 && this.newProduct.quantity);
            if (step === 2) return isStep1Complete;

            const isStep2Complete = !!(this.newProduct.target && this.newProduct.tax);
            if (step === 3) return isStep1Complete && isStep2Complete;

            if (step === 4) return isStep1Complete && isStep2Complete;

            return false;
        },
        nextStep() {
            if (this.currentStep < 4) {
                const nextStep = this.currentStep + 1;

                if (this.validateCurrentStep()) {
                    this.currentStep = nextStep;
                } else {
                    this.$swal.fire({
                        title: '입력 오류',
                        text: '필수 항목을 모두 입력해주세요.',
                        icon: 'warning',
                        confirmButtonColor: '#4a90e2',
                        confirmButtonText: '확인'
                    });
                }
            }
        },
        validateCurrentStep() {
            switch (this.currentStep) {
                case 1:
                    return !!(this.newProduct.name3 && this.newProduct.quantity);
                case 2:
                    return !!(this.newProduct.target && this.newProduct.tax);
                case 3:
                    return true; // 3단계는 선택 입력사항
                default:
                    return true;
            }
        },

        // -----------------------------------------------
        // 검색 메서드
        // -----------------------------------------------
        async handleSearch() {
            // 검색어가 비어있을 경우 전체 카테고리 표시
            if (!this.searchQuery.trim()) {
                this.filteredItems = this.allCategories.reduce((results, cat1) => {
                    // 카테고리1 추가
                    results.push({
                        name1: cat1.name,
                        name2: '',
                        type: 'category1',
                        num1: cat1.num
                    });

                    // 해당 카테고리1의 모든 서브카테고리 추가
                    if (cat1.subcategories) {
                        cat1.subcategories.forEach(cat2 => {
                            results.push({
                                name1: cat1.name,
                                name2: cat2.name,
                                type: 'category2',
                                num1: cat1.num,
                                num2: cat2.num
                            });
                        });
                    }
                    return results;
                }, []);
                return;
            }

            const searchQuery = this.searchQuery.toLowerCase();

            // 검색어가 있는 경우의 기존 검색 로직
            this.filteredItems = this.allCategories.reduce((results, cat1) => {
                // 카테고리1 검색에 매칭되는 경우
                if (cat1.name.toLowerCase().includes(searchQuery)) {
                    // 카테고리1 추가
                    results.push({
                        name1: cat1.name,
                        name2: '',
                        type: 'category1',
                        num1: cat1.num
                    });

                    // 해당 카테고리1의 모든 서브카테고리 추가
                    if (cat1.subcategories) {
                        cat1.subcategories.forEach(cat2 => {
                            results.push({
                                name1: cat1.name,
                                name2: cat2.name,
                                type: 'category2',
                                num1: cat1.num,
                                num2: cat2.num
                            });
                        });
                    }
                } else {
                    // 카테고리1이 검색어와 매칭되지 않더라도 서브카테고리 검색
                    if (cat1.subcategories) {
                        cat1.subcategories.forEach(cat2 => {
                            if (cat2.name.toLowerCase().includes(searchQuery)) {
                                // 검색어와 매칭되는 카테고리2가 있으면 카테고리1도 함께 추가
                                if (!results.some(item => item.num1 === cat1.num && item.type === 'category1')) {
                                    results.push({
                                        name1: cat1.name,
                                        name2: '',
                                        type: 'category1',
                                        num1: cat1.num
                                    });
                                }
                                // 해당 카테고리1의 모든 서브카테고리 추가
                                cat1.subcategories.forEach(subCat2 => {
                                    results.push({
                                        name1: cat1.name,
                                        name2: subCat2.name,
                                        type: 'category2',
                                        num1: cat1.num,
                                        num2: subCat2.num
                                    });
                                });
                            }
                        });
                    }
                }

                return results;
            }, []);

            // 중복 제거
            this.filteredItems = Array.from(new Set(this.filteredItems.map(JSON.stringify))).map(JSON.parse);
        },

        async selectItem(item) {
            try {
                // 검색 관련 상태 초기화
                this.searchQuery = '';
                this.showSearchResults = false;

                // 카테고리1 선택
                this.selectedCategory1 = item.num1;

                // 카테고리2 목록 업데이트
                await this.onCategory1Change();

                // 카테고리2가 있는 경우 선택
                if (item.type === 'category2') {
                    this.selectedCategory2 = item.num2;
                }

            } catch (error) {
                console.error('카테고리 선택 중 오류 발생:', error);
                this.$swal.fire({
                    title: '오류',
                    text: '카테고리 선택 중 오류가 발생했습니다.',
                    icon: 'error'
                });
            }
        },

        // 검색 결과 영역 외 클릭 시 결과 숨기기
        handleClickOutside(event) {
            if (!event.target.closest('.search-wrapper')) {
                this.showSearchResults = false;
            }
        },

        validateForm() {
            // 먼저 카테고리 선택 여부 확인
            if (!this.selectedCategory1) {
                this.$swal.fire({
                    title: '입력 오류',
                    text: '카테고리1을 선택해주세요.',
                    icon: 'warning',
                    confirmButtonColor: '#4a90e2',
                    confirmButtonText: '확인'
                });
                return false;
            }

            if (!this.selectedCategory2) {
                this.$swal.fire({
                    title: '입력 오류',
                    text: '카테고리2를 선택해주세요.',
                    icon: 'warning',
                    confirmButtonColor: '#4a90e2',
                    confirmButtonText: '확인'
                });
                return false;
            }

            const requiredFields = {
                'name3': '품목명',
                'quantity': '수량',
                'target': '대상',
                'tax': '과세구분'
            };

            for (const [field, label] of Object.entries(requiredFields)) {
                if (!this.newProduct[field]) {
                    this.$swal.fire({
                        title: '입력 오류',
                        text: `${label}을(를) 입력해주세요.`,
                        icon: 'warning',
                        confirmButtonColor: '#4a90e2',
                        confirmButtonText: '확인'
                    });
                    return false;
                }
            }

            if (this.newProduct.quantity <= 0) {
                this.$swal.fire({
                    title: '입력 오류',
                    text: '수량은 0보다 커야 합니다.',
                    icon: 'warning',
                    confirmButtonColor: '#4a90e2',
                    confirmButtonText: '확인'
                });
                return false;
            }

            return true;
        },

        addHashtag() {
            const tag = this.hashtagInput.trim();
            if (tag) {
                if (this.hashtags.includes(tag)) {
                    this.$swal.fire({
                        title: '중복된 해시태그',
                        text: '이미 존재하는 해시태그입니다.',
                        icon: 'warning',
                        confirmButtonColor: '#4a90e2',
                        confirmButtonText: '확인'
                    });
                } else {
                    this.hashtags.push(tag);
                    this.newProduct.hashtags = this.hashtags;
                    this.hashtagInput = '';
                }
            }
        },

        removeHashtag(index) {
            this.hashtags.splice(index, 1);
            this.newProduct.hashtags = this.hashtags;
        },

        // 제품 등록 메서드 수정
        async addNewProduct() {
            if (!this.validateForm()) {
                return;
            }

            try {
                // 합계 수량을 명시적으로 설정
                this.newProduct.all_quantity = this.computedAllQuantity;

                const formData = new FormData();

                // 선택된 카테고리1과 카테고리2의 이름을 찾음
                const category1 = this.allCategories.find(cat => cat.num === parseInt(this.selectedCategory1));
                const category2 = category1?.subcategories?.find(cat => cat.num === parseInt(this.selectedCategory2));

                // formData에 카테고리 이름 직접 추가
                formData.append('name0', category1?.name || '');
                formData.append('name1', category2?.name || '');

                // 이미지 처리 - 파일 업로드 또는 URL
                if (this.imageFile) {
                    formData.append('image', this.imageFile);
                } else if (this.selectedImageUrl) {
                    formData.append('imageUrl', this.selectedImageUrl);
                    formData.append('imageTitle', this.selectedImageTitle || '');
                }

                // 해시태그 배열을 하나의 문자열로 결합하여 배열에 담아 추가
                if (this.hashtags.length > 0) {
                    const combinedHashtags = this.hashtags.join(',');
                    formData.append('hashtags', JSON.stringify([combinedHashtags]));
                }

                // location1과 location2를 하이픈으로 연결하여 location 필드에 저장
                if (this.newProduct.location1 || this.newProduct.location2) {
                    formData.append('location', `${this.newProduct.location1 || ''}-${this.newProduct.location2 || ''}`);
                } else {
                    formData.append('location', '');
                }

                // 나머지 제품 정보 추가
                Object.keys(this.newProduct).forEach(key => {
                    if (key !== 'image' && key !== 'hashtags' && key !== 'name0' && key !== 'name1' &&
                        key !== 'location1' && key !== 'location2') {
                        formData.append(key, this.newProduct[key]);
                    }
                });

                // 카테고리 ID도 함께 전송
                formData.append('category1_id', this.selectedCategory1);
                formData.append('category2_id', this.selectedCategory2);

                const response = await this.$axios.post(
                    `${this.$apiUrls.hanqmedi}/registerProducts`,
                    formData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                );

                if (response.status === 201) {
                    await this.$swal.fire({
                        title: '성공',
                        text: '신규 품목이 성공적으로 등록되었습니다.',
                        icon: 'success',
                        confirmButtonColor: '#4a90e2',
                        confirmButtonText: '확인'
                    });

                    this.$emit('productAdded');
                    this.resetForm();
                    this.$emit('close');
                    this.$emit('closeModal');
                }
            } catch (error) {
                console.error('신규 품목 등록 오류:', error);
                this.$swal.fire('오류', '품목 등록 중 오류가 발생했습니다.', 'error');
            }
        },

        // 폼 리셋 메서드 수정
        resetForm() {
            this.newProduct = {
                name0: '', // 카테고리1
                name1: '', // 카테고리2
                name2: '', // 사용 x
                name3: '', // 품목명
                name4: '', // 제품명
                target: '', // 대상
                bcode: '', // 바코드
                code: '', // 코드
                price: 0, // 구입가격
                price2: 0, // 대여가격
                price3: 0, // 공급가격
                color: '', // 색상
                size: '', // 사이즈
                type: '낱개', // 품목 유형
                quantity: 0, // 수량  
                pieces_per_box: 0, // 박스당 개수
                piece_quantity: 0, // 낱개수량
                all_quantity: 0, // 합계수량
                note: '', // 비고
                company: '', // 거래처
                icode: '', // 보험코드
                companykey: this.$globalState.company_key,
                tax: '과세', // 과세구분
                hashtags: [],
                location1: '', // 재고위치1
                location2: '' // 재고위치2
            };
            this.fileName = '';
            this.imagePreview = null;
            this.imageFile = null;
            this.selectedImageUrl = null;
            this.selectedImageTitle = null;
            this.hashtags = [];
            this.hashtagInput = '';
            this.selectedCategory1 = '';
            this.selectedCategory2 = '';
        },

        // 이미지 검색 모달에서 이미지 선택 완료
        async onImageSelected(selectedImageData) {
            try {
                // 기존 파일 업로드 정보 초기화
                this.imageFile = null;
                this.fileName = '';

                // 선택된 이미지 URL과 제목 저장
                this.selectedImageUrl = selectedImageData.url;
                this.selectedImageTitle = selectedImageData.title;

                // 미리보기 설정 (썸네일 사용)
                this.imagePreview = selectedImageData.thumbnail;

                this.closeImageSearchModal();

                this.$swal.fire({
                    title: '성공',
                    text: '이미지가 선택되었습니다.',
                    icon: 'success',
                    timer: 1500,
                    showConfirmButton: false
                });

            } catch (error) {
                console.error('이미지 선택 처리 중 오류:', error);
                this.$swal.fire('오류', '이미지 선택 중 오류가 발생했습니다.', 'error');
            }
        },

        // 파일 업로드 처리 (기존 메서드 수정)
        handleImageChange(event) {
            const file = event.target.files[0];
            if (file) {
                // 파일 업로드 시 검색 이미지 정보 초기화
                this.selectedImageUrl = null;
                this.selectedImageTitle = null;

                // 파일 크기 체크 (예: 5MB 제한)
                if (file.size > 5 * 1024 * 1024) {
                    this.$swal.fire({
                        title: '파일 크기 초과',
                        text: '이미지 크기는 5MB 이하여야 합니다.',
                        icon: 'warning',
                        confirmButtonColor: '#4a90e2',
                        confirmButtonText: '확인'
                    });
                    return;
                }

                // 이미지 파일 타입 체크
                if (!file.type.startsWith('image/')) {
                    this.$swal.fire({
                        title: '잘못된 파일 형식',
                        text: '이미지 파일만 업로드 가능합니다.',
                        icon: 'warning',
                        confirmButtonColor: '#4a90e2',
                        confirmButtonText: '확인'
                    });
                    return;
                }

                this.fileName = file.name;
                this.imageFile = file;
                this.createImagePreview(file);
            } else {
                this.fileName = '';
                this.imagePreview = null;
                this.imageFile = null;
            }
        },

        createImagePreview(file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                // 이미지 최적화를 위한 처리
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');

                    // 최대 크기 설정 (예: 800x800)
                    const maxSize = 800;
                    let width = img.width;
                    let height = img.height;

                    // 이미지 크기 조정
                    if (width > height) {
                        if (width > maxSize) {
                            height *= maxSize / width;
                            width = maxSize;
                        }
                    } else {
                        if (height > maxSize) {
                            width *= maxSize / height;
                            height = maxSize;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;
                    ctx.drawImage(img, 0, 0, width, height);

                    // 최적화된 이미지 URL 생성
                    this.imagePreview = canvas.toDataURL('image/jpeg', 0.8);
                };
                img.src = e.target.result;
            };
            reader.readAsDataURL(file);
        },

        handleEscKey(event) {
            if (event.key === 'Escape') {
                this.$emit('closeModal');
            }
        }
    },
}
</script>